// Dark mode. Orthogonal to the `data-theme` colour worlds: this only flips
// `data-mode` on <html> ("light" | "dark"), the CSS does the rest.
// Init order: explicit user choice in localStorage → OS preference. While no
// explicit choice is stored we keep following the OS (matchMedia listener).
import { writable } from "svelte/store";

export type Mode = "light" | "dark";

const STORAGE_KEY = "wd-mode";

function stored(): Mode | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    /* localStorage unavailable (private mode) — fall back to the OS */
    return null;
  }
}

function prefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

function initial(): Mode {
  if (typeof window === "undefined") return "light";
  return stored() ?? (prefersDark() ? "dark" : "light");
}

function apply(m: Mode): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.mode = m;
}

export const mode = writable<Mode>(initial());

mode.subscribe(apply);

export function toggleMode(): void {
  mode.update((m) => {
    const next: Mode = m === "dark" ? "light" : "dark";
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore — the choice just won't survive a reload */
    }
    return next;
  });
}

// Follow the OS only as long as the visitor made no explicit choice.
if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  mq.addEventListener("change", (e) => {
    if (stored() === null) mode.set(e.matches ? "dark" : "light");
  });
}
